import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// PART 1: Water Purification Steps
// ============================================================================

const part1Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Label Beakers',
    shortDescription: 'Click beakers to label',
    handler: 'handleLabel',
    validation: {
      requires: [],
      condition: 'labeled === false',
    },
    infoContent: {
      title: 'Labeling Your Samples',
      content: `In scientific experiments, we always compare a CONTROL (unchanged) with an EXPERIMENTAL (treated) sample. This is called the scientific method - it allows us to isolate the effect of our treatment.

You are setting up two identical starting conditions. The control beaker will remain untreated throughout the experiment, serving as your baseline for comparison.

**What to Observe:**
- Control = No treatment (shows natural settling)
- Experimental = Will receive lime + alum treatment
- Both start with identical contaminated water`,
      whatHappens: 'Two beakers are labelled "Control" and "Experimental" to establish a controlled comparison for the water purification treatment.',
      observe: [
        'Label one beaker "Control" and the other "Experimental"',
        'The control beaker will receive no chemical treatment',
        'Both beakers will start with identical contaminated water for a fair comparison',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Water',
    shortDescription: 'Click experimental beaker',
    handler: 'handleWater',
    validation: {
      requires: ['labeled'],
      condition: 'labeled === true',
    },
    infoContent: {
      title: 'Adding Water Sample',
      content: `Fill the experimental beaker **three quarters full** with water. In water treatment plants, millions of liters are processed using the same principles you will learn.

The beaker fills with clear water. Notice the meniscus (curved surface) at the top - this is caused by adhesion between water molecules and glass. You will later pour half into the control beaker.

**What to Observe:**
- Water level should reach the three-quarter mark
- The meniscus curves upward at the edges
- Water appears clear before contamination`,
      whatHappens: 'The experimental beaker is filled three-quarters full with clean water, which will serve as the base for the contaminated water sample.',
      observe: [
        'Water level reaches approximately the three-quarter mark',
        'Note the concave meniscus where water meets the glass walls',
        'Water is clear and transparent before any contamination is added',
      ],
    },
  },
  {
    id: 3,
    title: 'Add Soil',
    shortDescription: 'Click soil container',
    handler: 'handleSoil',
    validation: {
      requires: ['beakers.experimental.water'],
      condition: 'beakers.experimental.water > 0',
    },
    infoContent: {
      title: 'Adding Soil Contaminants',
      content: `Add **10.0 g** of soil to the water. Soil contains suspended particles (clay, silt), organic matter, and dissolved minerals. These particles have a NEGATIVE electrical charge, which is why they repel each other and stay suspended instead of settling.

The soil disperses throughout the water, creating turbidity (cloudiness). The tiny particles are too small to settle on their own due to Brownian motion and electrostatic repulsion.

**What to Observe:**
- Water becomes brown/muddy immediately
- Particles remain suspended (don't settle)
- Light cannot pass through easily (turbid)`,
      whatHappens: 'Soil (10.0 g) is added to the water, introducing negatively charged clay and silt particles that remain suspended due to electrostatic repulsion and Brownian motion.',
      observe: [
        'Water turns brown/muddy immediately upon adding soil',
        'Particles remain suspended and do not settle on their own',
        'The solution becomes turbid and opaque to light',
      ],
    },
  },
  {
    id: 4,
    title: 'Stir',
    shortDescription: 'Click stirring rod',
    handler: 'handleStir',
    validation: {
      requires: ['beakers.experimental.muddy'],
      condition: 'beakers.experimental.muddy === true',
    },
    infoContent: {
      title: 'Stirring for Homogeneity',
      content: `Stirring ensures HOMOGENEOUS distribution - every part of the sample has the same concentration of contaminants. This is critical for fair comparison between control and experimental samples.

Mechanical agitation distributes particles evenly. Without stirring, heavier particles might concentrate at the bottom, giving unequal samples when divided.

**What to Observe:**
- Color becomes uniform throughout
- No settling visible during stirring
- Vortex forms in center of liquid`,
      whatHappens: 'Stirring distributes soil particles homogeneously throughout the water so that both control and experimental portions will have identical contaminant concentrations.',
      observe: [
        'Colour becomes uniform throughout the beaker with no gradient',
        'No visible settling occurs during continuous stirring',
        'A vortex forms in the centre of the liquid',
      ],
    },
  },
  {
    id: 5,
    title: 'Pour Half',
    shortDescription: 'Click experimental beaker',
    handler: 'handlePour',
    validation: {
      requires: ['stirred'],
      condition: 'stirred === true',
    },
    infoContent: {
      title: 'Creating Control & Experimental Samples',
      content: `Pour approximately half (~200 mL) of the contaminated water into the control beaker. By dividing the contaminated water equally, we ensure both samples start IDENTICAL. Any difference we observe later can only be attributed to the treatment we apply.

Both beakers now contain ~200 mL of identically contaminated water.

**What to Observe:**
- Both beakers have equal volume (~200 mL each)
- Both have identical color/turbidity
- Control will receive NO treatment`,
      whatHappens: 'The homogeneous contaminated water is split equally between two beakers to ensure both the control and experimental samples begin with identical conditions.',
      observe: [
        'Both beakers contain approximately 200 mL each',
        'Both beakers have identical colour and turbidity',
        'The control beaker will receive no further treatment',
      ],
    },
  },
  {
    id: 6,
    title: 'Add Lime',
    shortDescription: 'Add lime to experimental beaker',
    handler: 'handleLime',
    validation: {
      requires: ['poured'],
      condition: 'poured === true',
    },
    infoContent: {
      title: 'Adding Lime (Calcium Oxide)',
      content: `Add **1 dropper full of lime solution** (or a **match-head size amount of solid lime**) to the EXPERIMENTAL beaker of muddy water. Stir the mixture thoroughly.

Lime (CaO) reacts with water to produce a basic solution:

CaO(s) + H₂O(l) → Ca²⁺(aq) + 2OH⁻(aq)

This creates calcium hydroxide, making the solution BASIC (pH > 7). The basic environment is essential — alum works by forming Al(OH)₃ precipitate, which requires OH⁻ ions to be present. Without lime first, the alum cannot form the coagulant effectively.

**Note:** The amount of lime is not critical. If using solid CaO, you can make a solution by adding 1 tsp to 250 mL of water. The Ca(OH)₂ that forms does not dissolve well, and the excess will settle to the bottom.

**What to Observe:**
- Slight cloudiness as lime dissolves
- Solution may feel slightly warm
- pH increases to above 7 (becomes basic)
- Prepare for litmus test to confirm`,
      chemicalEquation: 'CaO(s) + H₂O(l) → Ca²⁺(aq) + 2OH⁻(aq)',
      whatHappens: 'Lime (CaO) reacts with water to produce calcium hydroxide, raising the pH above 7 to create the basic conditions required for alum coagulation.',
      observe: [
        'Slight increase in cloudiness as lime dissolves or disperses',
        'Solution may feel slightly warm due to the exothermic reaction',
        'The pH increases above 7, which will be confirmed by litmus paper in the next step',
      ],
    },
  },
  {
    id: 7,
    title: 'Test pH',
    shortDescription: 'Click litmus paper',
    handler: 'handleLitmus',
    validation: {
      requires: ['beakers.experimental.basic'],
      condition: 'beakers.experimental.basic === true',
    },
    infoContent: {
      title: 'Testing pH with Litmus Paper',
      content: `Dip a strip of **red litmus paper** into the solution. Litmus is a natural pH indicator extracted from lichens:

• Acidic (pH < 7): Stays RED
• Basic (pH > 7): Turns BLUE

If the litmus paper does **not** turn blue, add more lime solution, a little at a time, until it does. Red litmus paper turns blue in a basic solution, confirming the OH⁻ ions are present.

**What to Observe:**
- Red litmus paper turns BLUE
- Color change confirms pH > 7 (basic)
- This confirms lime reacted successfully
- If still red, add more lime and re-test
- Basic pH is required for coagulation`,
      whatHappens: 'Red litmus paper is dipped into the solution to confirm the pH is above 7 (basic), verifying that sufficient hydroxide ions are present for the alum coagulation step.',
      observe: [
        'Red litmus paper should turn blue upon contact with the solution',
        'If litmus remains red, more lime is needed before proceeding',
        'The colour change confirms OH⁻ ions are present in sufficient quantity',
      ],
    },
  },
  {
    id: 8,
    title: 'Add Alum',
    shortDescription: 'Add alum solution to experimental beaker',
    handler: 'handleAlum',
    validation: {
      requires: ['litmusColor'],
      condition: "litmusColor === 'blue'",
    },
    infoContent: {
      title: 'Adding Alum (Coagulant)',
      content: `Add **1 dropper full** of alum solution [KAl(SO₄)₂ or NH₄Al(SO₄)₂] to the mixture. Record your observation. **Continue adding alum until a thick, white, gelatinous precipitate forms.** Stir the water thoroughly.

Alum dissociates in water to release Al³⁺ ions. In basic conditions, these form aluminum hydroxide:

Al³⁺(aq) + 3OH⁻(aq) → Al(OH)₃(s)

The Al(OH)₃ precipitate is POSITIVELY charged and attracts the NEGATIVELY charged dirt particles!

This is COAGULATION — opposite charges attract! The positive Al(OH)₃ flocs attach to negative dirt particles, neutralizing their charge so they can clump together (FLOCCULATION).

**Note:** Alum solution is prepared by adding 3-4 tsp of aluminum sulfate, potassium aluminum sulfate, or ammonium aluminum sulfate to 1 L of water.

**What to Observe:**
- White, gelatinous precipitate forms (Al(OH)₃)
- Particles begin clumping together into visible flocs
- Solution may look more cloudy initially
- Continue adding alum until precipitate is thick and gelatinous`,
      chemicalEquation: 'Al³⁺(aq) + 3OH⁻(aq) → Al(OH)₃(s)',
      whatHappens: 'Alum dissociates to release Al3+ ions which react with OH- to form positively charged Al(OH)3 precipitate that attracts and neutralizes the negatively charged dirt particles (coagulation).',
      observe: [
        'A white, gelatinous precipitate of Al(OH)3 forms',
        'Suspended dirt particles begin clumping together into visible flocs',
        'Continue adding alum until the precipitate is thick and gelatinous',
        'The solution may appear more cloudy initially before flocs become visible',
      ],
    },
  },
  {
    id: 9,
    title: 'Settle',
    shortDescription: 'Start timer and wait',
    handler: 'handleTimer',
    validation: {
      requires: ['beakers.experimental.precipitate'],
      condition: 'beakers.experimental.precipitate >= 50',
    },
    infoContent: {
      title: 'Sedimentation (Settling)',
      content: `Allow both beakers to settle **undisturbed** for **10-15 minutes**. Do not move or bump the beakers during settling!

Gravity pulls the heavy flocs downward — this is SEDIMENTATION. The rate of settling follows Stokes' Law: larger, denser particles settle faster. The flocs we created are much larger than individual dirt particles.

Over time, the flocs sink to the bottom, forming a layer of SEDIMENT. The water above gradually becomes clearer as particles are removed.

**What to Observe:**
- Flocs slowly sink to bottom of EXPERIMENTAL beaker
- Sediment layer forms at base
- Water clarity improves from top down
- CONTROL beaker: remains turbid with minimal settling
- Process takes 10-15 minutes (30+ minutes in real treatment plants)`,
      whatHappens: 'Gravity pulls the large, heavy flocs downward (sedimentation), and a sediment layer forms at the bottom while the water above gradually clears.',
      observe: [
        'Flocs visibly sink toward the bottom of the experimental beaker',
        'A distinct sediment layer accumulates at the base over 10-15 minutes',
        'Water clarity improves progressively from the top downward',
        'The control beaker remains turbid with minimal natural settling',
      ],
    },
  },
  {
    id: 10,
    title: 'Compare',
    shortDescription: 'Observe results',
    handler: 'handleCompare',
    validation: {
      requires: ['beakers.experimental.cleared'],
      condition: 'beakers.experimental.cleared === true',
    },
    infoContent: {
      title: 'Comparing Results',
      content: `This is the critical evaluation step. The CONTROL shows what happens naturally (minimal settling due to particle repulsion). The EXPERIMENTAL shows the effect of coagulation treatment.

Compare both beakers side by side. The treated water should be significantly clearer, demonstrating that coagulation and flocculation successfully removed suspended particles.

**What to Observe:**
- CONTROL: Still turbid, minimal settling
- EXPERIMENTAL: Much clearer water
- EXPERIMENTAL: Visible sediment layer
- Treatment removed suspended solids!

**Comparison:**
| Observation | Control | Treated |
|-------------|---------|---------|
| Clarity | Cloudy/turbid | Clear or nearly clear |
| Sediment | Little to none | Thick layer at bottom |
| Color | Brown throughout | Light/clear on top |`,
      whatHappens: 'The treated (experimental) and untreated (control) beakers are compared side by side to evaluate the effectiveness of the lime-alum coagulation and flocculation process.',
      observe: [
        'Control beaker remains cloudy and turbid with minimal settling',
        'Experimental beaker shows much clearer water above a thick sediment layer',
        'The dramatic difference demonstrates that coagulation effectively removed suspended particles',
      ],
    },
  },
];

// ============================================================================
// PART 1: Initial State
// ============================================================================

const part1InitialState = {
  labeled: false,
  stirred: false,
  poured: false,
  litmusColor: null as string | null,
  settlingProgress: 0,
  // Quantitative specifications from lab manual
  specs: {
    waterFillLevel: 'three quarters full', // fill experimental beaker 3/4 full
    soilQuantityGrams: 10.0,     // grams of soil (exact: 10.0 g)
    limeAmount: '1 dropper full of lime solution (or match-head size solid CaO)',
    alumAmount: '1 dropper full initially, continue adding until thick white gelatinous precipitate forms',
    settlingTimeMinutes: 'several minutes observation', // observe side-by-side
    litmusType: 'red' as const,  // RED litmus paper specifically
    limePrep: '1 tsp CaO to 250 mL water (amount not critical)',
    alumPrep: '3-4 tsp aluminum sulfate to 1 L water (amount not critical)',
  },
  beakers: {
    control: {
      water: 0,
      muddy: false,
    },
    experimental: {
      water: 0,
      muddy: false,
      basic: false,
      lime: 0,
      alum: 0,
      precipitate: 0,
      cleared: false,
    },
  },
  // Expected observations from lab manual
  expectedResults: {
    controlBeaker: 'Remains cloudy/muddy with minimal natural settling',
    experimentalBeaker: 'Much clearer water with visible sediment layer at bottom',
    duringStirring: 'Flocs (aggregated particles) become visible as white clumps',
    flocAppearance: 'White, fluffy precipitate of Al(OH)₃',
    litmusResult: 'Red litmus paper turns BLUE (confirms pH > 7)',
  },
};

// ============================================================================
// PART 2: Water Analysis Tests Configuration
// ============================================================================

export interface WaterSampleData {
  name: string;
  conductivity: string;
  hardness: string;
  chloride: string;
  sulfate: string;
  flameColor: string;
  flameElement: string;
}

// Water samples from lab manual: (a) tap water, (b) 0.1 M NaCl, (c) distilled water,
// (d) laboratory reverse osmosis water, (e) spring water, (f) hydrogen ion exchange water,
// (g) Brita filtered water, and (h) possibly home tap water.
export const waterSampleData: Record<string, WaterSampleData> = {
  tapWater: {
    name: '(a) Tap Water',
    conductivity: 'High',
    hardness: 'Medium',
    chloride: 'Present',
    sulfate: 'Present',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)',
  },
  naclSolution: {
    name: '(b) 0.1 M NaCl',
    conductivity: 'Very High',
    hardness: 'None',
    chloride: 'Very High',
    sulfate: 'Absent',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)',
  },
  distilledWater: {
    name: '(c) Distilled Water',
    conductivity: 'Very Low',
    hardness: 'None',
    chloride: 'Absent',
    sulfate: 'Absent',
    flameColor: 'No color',
    flameElement: 'None detected',
  },
  reverseOsmosisWater: {
    name: '(d) Laboratory Reverse Osmosis Water',
    conductivity: 'Very Low',
    hardness: 'None',
    chloride: 'Absent',
    sulfate: 'Absent',
    flameColor: 'No color',
    flameElement: 'None detected',
  },
  springWater: {
    name: '(e) Spring Water',
    conductivity: 'Medium',
    hardness: 'High',
    chloride: 'Present',
    sulfate: 'Present',
    flameColor: 'Lilac',
    flameElement: 'Potassium (K)',
  },
  hydrogenExchangeWater: {
    name: '(f) Hydrogen Ion Exchange Water',
    conductivity: 'Low',
    hardness: 'Very Low',
    chloride: 'Present',
    sulfate: 'Present',
    flameColor: 'No color',
    flameElement: 'None detected',
  },
  britaFilteredWater: {
    name: '(g) Brita Filtered Water',
    conductivity: 'Medium',
    hardness: 'Low',
    chloride: 'Present',
    sulfate: 'Present',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)',
  },
  homeTapWater: {
    name: '(h) Home Tap Water (optional)',
    conductivity: 'Medium-High',
    hardness: 'Medium',
    chloride: 'Present',
    sulfate: 'Present',
    flameColor: 'Yellow-Orange',
    flameElement: 'Sodium (Na)',
  },
};

export interface TestInfo {
  title: string;
  why: string;
  how: string;
  when: string;
}

export const testInfoData: Record<string, TestInfo> = {
  conductivity: {
    title: 'Conductivity Test',
    why: 'Conductivity measures how well water conducts electricity, which indicates the presence of dissolved ions (electrolytes). Pure water has very low conductivity.',
    how: 'Electrodes measure electrical current flow through the sample. More ions = higher conductivity.',
    when: 'Use to quickly assess water purity and mineral content.',
  },
  hardness: {
    title: 'Hardness Test (EDTA Dropper Titration)',
    why: 'Water hardness is a measure of the combined amounts of calcium and magnesium salts present. As soap consists of fatty acid salts (e.g., sodium stearate) and the fatty acid salts of calcium and magnesium are insoluble in water, the effectiveness of soap decreases as hardness increases.',
    how: 'Pipet **5.00 mL** of water sample into a large test tube. Add **4 drops** of ammonia-ammonium chloride buffer and **1 drop** of eriochrome black T indicator. Stir. Titrate with **0.00500 M EDTA** using a dropper, counting drops needed for color change from **pink-red to blue**. Hardness (ppm) ≈ (5)(# of drops). Range: 0-100 drops depending on sample.',
    when: 'Perform on all water samples to compare purification effectiveness.',
  },
  chloride: {
    title: 'Chloride & Sulfate Precipitate Tests',
    why: 'Tests for the formation of precipitates to detect chloride and sulfate ions in water samples.',
    how: 'Add a few drops of **0.1 M BaCl₂** to about **0.5 mL** of each sample in **13×100 mm test tubes** to test for sulfates. Repeat with **0.1 M AgNO₃** on new samples to test for chlorides. Ag⁺ + Cl⁻ → AgCl↓ (white precipitate). Ba²⁺ + SO₄²⁻ → BaSO₄↓ (white precipitate).',
    when: 'Perform on all water samples to compare ion content.',
  },
  sulfate: {
    title: 'Sulfate Test (BaCl₂)',
    why: 'Sulfate ions indicate mineral content, industrial contamination, or natural geological sources. High sulfate can cause a bitter taste and laxative effects.',
    how: 'Barium chloride reacts with sulfate to form a white barium sulfate precipitate: Ba²⁺ + SO₄²⁻ → BaSO₄↓. The precipitate is insoluble in acid.',
    when: 'Test to detect sulfate contamination or evaluate mineral content in water sources.',
  },
  flame: {
    title: 'Flame Test',
    why: 'Different metal ions produce characteristic flame colors due to electron excitation. This identifies specific elements.',
    how: 'First clean a **platinum or nichrome wire** by alternately dipping it into **6 M HCl** and holding it in a flame until no significant colour is observed. Put a few drops of each sample into **clean test tubes** and then test each water sample in the flame, being sure to clean the wire between each test. **Do not put the wire into the original water samples** as the HCl on the wire will contaminate the samples.',
    when: 'Perform on all water samples to identify alkali and alkaline earth metals.',
  },
};

// Part 2 steps - analysis tests for each sample
const part2Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Select Water Sample',
    shortDescription: 'Choose a water sample to test',
    handler: 'handleSelectSample',
    infoContent: {
      title: 'Selecting Your Sample',
      content: `You will analyze **eight** different water samples:
- **(a) Tap Water:** Contains dissolved minerals from municipal treatment
- **(b) 0.1 M NaCl:** Simulated salt solution for comparison
- **(c) Distilled Water:** Purified through distillation (very pure, minimal ions)
- **(d) Laboratory Reverse Osmosis Water:** Purified by reverse osmosis membrane
- **(e) Spring Water:** Naturally occurring water with dissolved minerals
- **(f) Hydrogen Ion Exchange Water:** Prepared by passing tap water through hydrogen exchange resin
- **(g) Brita Filtered Water:** Carbon-filtered tap water
- **(h) Home Tap Water (optional):** Brought from home for comparison

**Note:** All water samples are ready to be tested except for **Hydrogen Exchange Resin filtered water**, which must first be prepared:
1. Set up a long stem funnel for gravity filtration with an appropriately sized piece of filter paper
2. Fill to the 1/3 level with a hydrogen exchange resin
3. Pass the water through the resin and collect about 20 mL of tap water

Each sample will be tested with four methods: conductivity, chlorides & sulfates (precipitate tests), flame test, and water hardness (EDTA dropper titration).`,
      whatHappens: 'Eight different water samples from various sources and purification methods are selected for comparative analysis of their dissolved ion content.',
      observe: [
        'Note the visual appearance of each water sample (all should appear clear)',
        'Hydrogen exchange resin water must be prepared fresh by gravity filtration through resin',
        'Label all samples carefully to avoid cross-contamination during testing',
      ],
    },
  },
  {
    id: 2,
    title: 'Conductivity Test',
    shortDescription: 'Measure electrical conductivity',
    handler: 'runTest:conductivity',
    infoContent: {
      title: 'Conductivity Test',
      content: `Conductivity measures how well water conducts electricity, which indicates the presence of dissolved ions (electrolytes). Pure water has very low conductivity.

**How it works:** Electrodes measure electrical current flow through the sample. More ions = higher conductivity.

**When to use:** Use to quickly assess water purity and mineral content.`,
      whatHappens: 'Electrodes immersed in each water sample measure electrical conductivity, which is directly proportional to the concentration of dissolved ions.',
      observe: [
        'Distilled and reverse osmosis water show very low conductivity readings',
        '0.1 M NaCl solution shows the highest conductivity due to abundant Na+ and Cl- ions',
        'Tap and spring water show intermediate conductivity from dissolved minerals',
      ],
    },
  },
  {
    id: 3,
    title: 'Hardness Test',
    shortDescription: 'Test water hardness with EDTA',
    handler: 'runTest:hardness',
    infoContent: {
      title: 'Hardness Test (EDTA)',
      content: `Water hardness is a measure of the combined amounts of calcium and magnesium salts present. The total Ca²⁺ and Mg²⁺ concentration is determined by titration with EDTA, which chelates with the metal ions in a 1:1 ratio.

**Procedure:**
1. Pipet **5.00 mL** of water sample into a large test tube
2. Add **4 drops** of ammonia-ammonium chloride buffer
3. Add **1 drop** of eriochrome black T indicator and stir
4. Titrate with **0.00500 M EDTA** using a dropper
5. Count drops needed for color change: **pink-red → blue**

**Hardness Calculation:**
hardness (ppm) ≈ (5)(number of drops)

The indicator forms a pink-red complex with Ca²⁺. As EDTA is added, it chelates Ca²⁺, leaving uncomplexed indicator which is blue. Range: 0-100 drops depending on sample.`,
      whatHappens: 'EDTA chelates Ca2+ and Mg2+ in a 1:1 ratio; as EDTA displaces the metal from the eriochrome black T indicator, the colour changes from pink-red to blue at the endpoint.',
      observe: [
        'Solution turns pink-red after adding eriochrome black T indicator and buffer',
        'Count each drop of EDTA carefully as it is added from the dropper',
        'Endpoint is reached when the solution turns distinctly blue and remains stable',
        'Distilled and RO water require very few or no drops; spring water requires many more',
      ],
    },
  },
  {
    id: 4,
    title: 'Chloride Test',
    shortDescription: 'Detect chloride with AgNO₃',
    handler: 'runTest:chloride',
    infoContent: {
      title: 'Chloride Test (AgNO₃)',
      content: `Test for the formation of precipitates in all the water samples.

**Procedure:**
1. Add a few drops of **0.1 M BaCl₂** to about **0.5 mL** of each sample in **13×100 mm test tubes** — tests for sulfates
2. Repeat this test on **new samples** of each water type using **0.1 M AgNO₃** — tests for chlorides

**Reactions:**
- Ba²⁺ + SO₄²⁻ → BaSO₄↓ (white precipitate = sulfate present)
- Ag⁺ + Cl⁻ → AgCl↓ (white precipitate = chloride present)`,
      chemicalEquation: 'Ag⁺ + Cl⁻ → AgCl↓; Ba²⁺ + SO₄²⁻ → BaSO₄↓',
      whatHappens: 'Silver nitrate reacts with chloride ions to form an insoluble white AgCl precipitate, confirming the presence of chloride in the water sample.',
      observe: [
        'A white precipitate (AgCl) forms in samples containing chloride ions',
        'No precipitate forms in distilled or reverse osmosis water',
        '0.1 M NaCl shows a very heavy white precipitate due to high chloride concentration',
      ],
    },
  },
  {
    id: 5,
    title: 'Sulfate Test',
    shortDescription: 'Detect sulfate with BaCl₂',
    handler: 'runTest:sulfate',
    infoContent: {
      title: 'Sulfate Test (BaCl₂)',
      content: `The sulfate test uses **0.1 M BaCl₂** added to about **0.5 mL** of each sample in **13×100 mm test tubes**.

Ba²⁺ + SO₄²⁻ → BaSO₄↓ (white precipitate)

A white precipitate of barium sulfate indicates the presence of sulfate ions. Unlike AgCl, barium sulfate is also insoluble in dilute acid, confirming the presence of sulfate specifically.

**Note:** This test is combined with the chloride test (using **0.1 M AgNO₃**) to detect both anion types.`,
      chemicalEquation: 'Ba²⁺ + SO₄²⁻ → BaSO₄↓',
      whatHappens: 'Barium chloride reacts with sulfate ions to form an insoluble white BaSO4 precipitate; unlike AgCl, barium sulfate is also insoluble in dilute acid.',
      observe: [
        'A white precipitate (BaSO4) forms in samples containing sulfate ions',
        'No precipitate in distilled water or 0.1 M NaCl (which lacks sulfate)',
        'Tap and spring water may show faint to moderate precipitate depending on mineral content',
      ],
    },
  },
  {
    id: 6,
    title: 'Flame Test',
    shortDescription: 'Identify metal ions by flame color',
    handler: 'runTest:flame',
    infoContent: {
      title: 'Flame Test',
      content: `Different metal ions produce characteristic flame colors due to electron excitation.

**Procedure:**
1. First clean a **platinum or nichrome wire** by alternately dipping it into **6 M HCl** and holding it in a flame until no significant colour is observed
2. Put a few drops of each sample into **clean test tubes**
3. Test each water sample in the flame, cleaning the wire between each test
4. **Do not put the wire into the original water samples** as the HCl on the wire will contaminate the samples

**Common flame colors:**
- Sodium (Na): Yellow-Orange
- Potassium (K): Lilac
- Calcium (Ca): Brick Red
- Copper (Cu): Blue-Green

**How it works:** Electrons absorb energy from the flame, jump to higher orbitals, then emit light of characteristic wavelengths as they fall back.`,
      whatHappens: 'Metal ions in the water sample absorb thermal energy from the Bunsen burner flame, exciting electrons to higher energy levels; as they relax, they emit photons of characteristic wavelengths visible as distinct flame colours.',
      observe: [
        'Clean the nichrome wire until no colour is produced in the flame before each test',
        'Tap water and NaCl produce a strong yellow-orange flame (sodium)',
        'Spring water may show a lilac flame (potassium)',
        'Distilled and RO water produce no significant flame colour',
      ],
    },
  },
];

// ============================================================================
// PART 2: Initial State
// ============================================================================

const part2InitialState = {
  selectedSample: 'tapWater',
  testRunning: false,
  activeTest: null as string | null,
  flameActive: false,
  results: {} as Record<string, Partial<WaterSampleData>>,
  testResults: {
    conductivity: {} as Record<string, boolean>,
    hardness: {} as Record<string, boolean>,
    chloride: {} as Record<string, boolean>,
    sulfate: {} as Record<string, boolean>,
    flame: {} as Record<string, boolean>,
  },
};

// ============================================================================
// EXPERIMENT PARTS DEFINITION
// ============================================================================

const part1: ExperimentPart = {
  id: 1,
  name: 'part1',
  title: 'Part I: Water Purification',
  steps: part1Steps,
  equipment: [
    'Two large beakers or jars (control + experimental)',
    'Soil (10.0 g)',
    'Lime solution (CaO — 1 tsp to 250 mL water) or solid CaO',
    'Alum solution (KAl(SO₄)₂ or NH₄Al(SO₄)₂ — 3-4 tsp to 1 L water)',
    'Red litmus paper',
    'Stirring rod',
  ],
  initialState: part1InitialState,
};

const part2: ExperimentPart = {
  id: 2,
  name: 'part2',
  title: 'Part II: Water Quality Analysis',
  steps: part2Steps,
  equipment: [
    '13×100 mm test tubes (multiple)',
    'Large test tubes (for EDTA titration)',
    'LED conductivity device or commercial conductivity apparatus with probe',
    'Bunsen burner',
    'Platinum or nichrome wire (for flame test)',
    '6 M HCl (for cleaning flame test wire)',
    '0.1 M BaCl₂ solution (sulfate test)',
    '0.1 M AgNO₃ solution (chloride test)',
    '0.00500 M EDTA solution (in dropper/dropper bottle)',
    'Ammonia-ammonium chloride buffer',
    'Eriochrome black T indicator',
    '5.00 mL pipet',
    'Long stem funnel with filter paper (for H⁺ exchange resin)',
    'Hydrogen exchange resin',
    '0.1 M NaCl solution (water sample b)',
  ],
  initialState: part2InitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

export const experiment2Config: ExperimentConfig = {
  id: 'exp2',
  number: 2,
  name: 'Water Purification',
  description:
    'Purify muddy water using lime and alum treatment, then analyze eight different water samples using conductivity, precipitate tests (BaCl₂ and AgNO₃), flame tests, and EDTA hardness titration. No formal report required.',
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    part1,
    part2,
  ],
  requiredEquipment: [
    'Two large beakers or jars',
    'Soil (10.0 g)',
    'Lime (CaO) solution or solid CaO',
    'Alum solution (KAl(SO₄)₂ or NH₄Al(SO₄)₂)',
    'Red litmus paper',
    'Stirring rod',
    '13×100 mm test tubes (multiple)',
    'Large test tubes (for EDTA titration)',
    'LED conductivity device or conductivity apparatus with probe',
    'Bunsen burner',
    'Platinum or nichrome wire',
    '6 M HCl (for cleaning flame test wire)',
    '0.1 M BaCl₂ solution',
    '0.1 M AgNO₃ solution',
    '0.00500 M EDTA solution (dropper bottle)',
    'Ammonia-ammonium chloride buffer',
    'Eriochrome black T indicator',
    '5.00 mL pipet',
    'Long stem funnel with filter paper',
    'Hydrogen exchange resin',
    '0.1 M NaCl solution',
  ],
  estimatedTime: '60-90 minutes',
  learningObjectives: [
    'Understand the role of a control group in scientific methodology',
    'Understand the principles of coagulation and flocculation in water treatment',
    'Explain why lime (basic conditions) is necessary before alum addition',
    'Learn the role of pH in chemical reactions (lime creates basic conditions)',
    'Observe precipitation reactions with aluminum hydroxide (Al(OH)₃)',
    'Describe the relationship between particle size and settling velocity (Stokes\' Law)',
    'Compare treated vs untreated water to understand experimental controls',
    'Use conductivity testing to assess dissolved ion content',
    'Perform EDTA titration to measure water hardness (Ca²⁺/Mg²⁺)',
    'Detect chloride ions using silver nitrate precipitation (Ag⁺ + Cl⁻ → AgCl)',
    'Detect sulfate ions using barium chloride precipitation (Ba²⁺ + SO₄²⁻ → BaSO₄)',
    'Identify metal ions through flame test spectroscopy using nichrome wire',
    'Distinguish between hard water and soft water based on mineral content',
    'Compare water quality across different sources using multiple test methods',
  ],
  safetyNotes: [
    'Wear safety goggles when handling chemicals',
    'Lime (CaO) is caustic - avoid skin contact, causes burns',
    'Silver nitrate (AgNO₃, 0.1 M) stains skin black and clothing permanently',
    'Barium chloride (BaCl₂, 0.1 M) is toxic - handle with care, avoid ingestion',
    '6 M HCl is concentrated - use with care when cleaning flame test wire',
    'Use caution around the Bunsen burner flame',
    'Do not put flame test wire into original water samples (HCl will contaminate)',
    'Wash hands thoroughly after the experiment',
  ],
  // Assessment questions from lab manual (Part I: Q1-4, Part II: Q5-9):
  // 1. How does the treated water sample compare with the control (untreated) sample?
  // 2. How do you know that a basic solution was produced when lime was added to the muddy water?
  // 3. Why was it necessary to make the sample basic?
  // 4. What happened when alum was added to the water?
  // 5. Identify and explain the conductivities of water samples with the highest and lowest conductivities.
  // 6. Identify the compounds referred to as precipitates in the chlorides and sulphates test.
  // 7. Why do different elements emit different colours when subject to intense heat?
  // 8. Calculate the ppm concentration of a 0.025 M CaCO₃ solution.
  // 9. Calculate the molar concentration of a 725 ppm solution of CaCO₃.
  quiz: {
    experimentId: 'exp2',
    title: 'Water Purification & Analysis Quiz',
    questions: [
      {
        id: 'exp2-q1',
        type: 'mcq' as const,
        question: 'Why is a control (untreated) sample maintained alongside the experimental sample?',
        options: [
          { id: 'a', text: 'To have extra water available' },
          { id: 'b', text: 'To compare the effects of treatment against an untreated baseline' },
          { id: 'c', text: 'To test different chemicals' },
          { id: 'd', text: 'To dilute the experimental sample' },
        ],
        correctOptionId: 'b',
        explanation: 'A control provides an untreated baseline for comparison, allowing us to isolate and measure the effect of the purification treatment.',
      },
      {
        id: 'exp2-q2',
        type: 'mcq' as const,
        question: 'What is the role of alum in water purification?',
        options: [
          { id: 'a', text: 'It kills bacteria' },
          { id: 'b', text: 'It acts as a coagulant, causing suspended particles to clump together and settle' },
          { id: 'c', text: 'It adds minerals to the water' },
          { id: 'd', text: 'It removes dissolved oxygen' },
        ],
        correctOptionId: 'b',
        explanation: 'Alum (aluminum sulfate) acts as a coagulant/flocculant. It neutralizes charges on suspended particles, causing them to aggregate into larger clumps (flocs) that settle out of solution.',
      },
      {
        id: 'exp2-q3',
        type: 'calculation' as const,
        question: 'Calculate the ppm concentration of a 0.025 M CaCO₃ solution. (Molar mass of CaCO₃ = 100.09 g/mol)',
        expectedValue: 2502,
        tolerance: 3,
        unit: 'ppm',
        explanation: 'ppm = Molarity × Molar Mass × 1000 = 0.025 × 100.09 × 1000 = 2502 ppm',
      },
      {
        id: 'exp2-q4',
        type: 'short_answer' as const,
        question: 'Why do different elements emit different colours when subjected to intense heat (flame test)?',
        sampleAnswer: 'Different elements have unique electron configurations with different energy level spacings. When heated, electrons are excited to higher energy levels. As they return to lower levels, they emit photons with specific wavelengths corresponding to those energy gaps. Since each element has unique energy level spacings, each produces a characteristic set of colours.',
      },
      {
        id: 'exp2-q5',
        type: 'calculation' as const,
        question: 'Calculate the molar concentration of a 725 ppm solution of CaCO₃. (Molar mass = 100.09 g/mol)',
        expectedValue: 0.00724,
        tolerance: 3,
        unit: 'M',
        explanation: 'Molarity = ppm / (Molar Mass × 1000) = 725 / (100.09 × 1000) = 0.00724 M',
      },
    ],
  },
};

export default experiment2Config;
